import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import Button from 'primevue/button'
import router from './router'
import App from './App.vue'
import './style.css'

const i18n = createI18n({
  legacy: false,
  locale: 'ar',
  fallbackLocale: 'en',
  messages: {
    en: {
      home: 'Home',
      about: 'About',
      lawyers: { title: 'Lawyers' },
      practices: 'Practice Areas',
      reviews: 'Reviews',
      contact: 'Contact Us',
      // Office dashboard
      officeManagement: 'Office Management',
      overview: 'Overview',
      statsAndIncome: 'Statistics & Income',
      filter: 'Filter',
      add: 'Add',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View',
      create: 'Create',
      refresh: 'Refresh',
      remove: 'Remove',
      manage: 'Manage',
      select: 'Select',
      required: 'Required',
      optional: 'Optional',
      loading: 'Loading...',
      noData: 'No data available',
      showing: 'Showing',
      of: 'of',
      search: 'Search',
      actions: 'Actions',
      status: 'Status',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      description: 'Description',
      notes: 'Notes',
      type: 'Type',
      amount: 'Amount',
      active: 'Active',
      inactive: 'Inactive',
      open: 'Open',
      closed: 'Closed',
      settled: 'Settled',
      inProgress: 'In Progress',
      draft: 'Draft',
      published: 'Published',
      live: 'Live',
      publish: 'Publish',
      activate: 'Activate',
      deactivate: 'Deactivate',
      // Clients
      clients: 'Clients',
      client: 'Client',
      recentClients: 'Recent Clients',
      searchClients: 'Search clients...',
      noClientsFound: 'No clients found',
      addClient: 'Add Client',
      editClient: 'Edit Client',
      firstName: 'First Name',
      fatherName: 'Father Name',
      thirdName: 'Third Name',
      familyName: 'Family Name',
      nationality: 'Nationality',
      person: 'Person',
      company: 'Company',
      companyName: 'Company Name',
      contactPerson: 'Contact Person',
      clientAccount: 'Client Account',
      clientAccountHint: 'Create login credentials for this client',
      username: 'Username',
      password: 'Password',
      confirm_password: 'Confirm Password',
      password_min_length: 'Password must be at least 6 characters',
      passwords_do_not_match: 'Passwords do not match',
      fillAllAccountFields: 'Please fill all account fields',
      // Staff
      staff: 'Staff',
      searchStaff: 'Search staff...',
      noStaffFound: 'No staff found',
      addStaff: 'Add Staff',
      addEmployee: 'Add Employee',
      fillEmployeeDetails: 'Fill in the employee details',
      personalInfo: 'Personal Information',
      middleName: 'Middle Name',
      lastName: 'Last Name',
      gender: 'Gender',
      male: 'Male',
      female: 'Female',
      dateOfBirth: 'Date of Birth',
      nationalId: 'National ID',
      contactInfo: 'Contact Information',
      cellNumber: 'Cell Number',
      personalEmail: 'Personal Email',
      employmentDetails: 'Employment Details',
      employeeId: 'Employee ID',
      autoGenerated: 'Auto-generated',
      designation: 'Designation',
      dateOfJoining: 'Date of Joining',
      grade: 'Grade',
      contractType: 'Contract Type',
      fullTime: 'Full Time',
      partTime: 'Part Time',
      contract: 'Contract',
      temporary: 'Temporary',
      onLeave: 'On Leave',
      terminated: 'Terminated',
      country: 'Country',
      workLocation: 'Work Location',
      salaryInfo: 'Salary Information',
      basicSalary: 'Basic Salary',
      salary: 'Salary',
      salaryCurrency: 'Currency',
      salaryMode: 'Salary Mode',
      bankName: 'Bank Name',
      bankAccountNo: 'Bank Account No.',
      searchByName: 'Search by name...',
      allStatuses: 'All Statuses',
      role: 'Role',
      selectRole: 'Select Role',
      hireDate: 'Hire Date',
      department: 'Department',
      failedToAddStaff: 'Failed to add staff',
      // Appointments
      appointments: {
        title: 'Appointments',
        date: 'Date',
        time: 'Time',
        location: 'Location',
        scheduled: 'Scheduled',
      },
      searchAppointments: 'Search appointments...',
      noAppointmentsFound: 'No appointments found',
      upcomingAppointments: 'Upcoming Appointments',
      // Courses
      courses: 'Courses',
      searchCourses: 'Search courses...',
      noCoursesFound: 'No courses found',
      noCoursesYet: 'No courses yet',
      addCourse: 'Add Course',
      addFirstCourse: 'Add First Course',
      editCourse: 'Edit Course',
      courseTitle: 'Course Title',
      duration: 'Duration',
      price: 'Price',
      discount: 'Discount',
      discountPercent: 'Discount %',
      free: 'Free',
      freeCourse: 'Free Course',
      enrollments: 'Enrollments',
      currency: 'Currency',
      // Resources
      resources: { title: 'Resources' },
      searchResources: 'Search resources...',
      noResourcesFound: 'No resources found',
      resourceType: 'Type',
      resourceSize: 'Size',
      resourceUploadDate: 'Upload Date',
      // Reports
      reports: 'Reports',
      searchReports: 'Search reports...',
      noReportsFound: 'No reports found',
      reportType: 'Report Type',
      reportPeriod: 'Period',
      reportGenerated: 'Generated',
      revenue: 'Revenue',
      avgRevenue: 'Avg Revenue',
      totalIncome: 'Total Income',
      // Transactions
      transactions: 'Transactions',
      searchTransactions: 'Search transactions...',
      noTransactionsFound: 'No transactions found',
      txIncome: 'Income',
      txExpenses: 'Expenses',
      txNet: 'Net',
      txAllTypes: 'All Types',
      txDescription: 'Description',
      txType: 'Type',
      txCategory: 'Category',
      txDate: 'Date',
      // Content
      contentManagement: 'Content',
      searchContent: 'Search content...',
      noContentFound: 'No content found',
      contentPage: 'Page',
      contentSection: 'Section',
      contentRequired: 'Content is required',
      contentLastUpdated: 'Last Updated',
      contentAdd: 'Add Content',
      contentEdit: 'Edit Content',
      contentDelete: 'Delete Content',
      contentDeleteConfirm: 'Are you sure you want to delete "{title}"?',
      contentTitle: 'Title',
      contentSubtitle: 'Subtitle',
      contentBody: 'Body',
      contentImageUrl: 'Image URL',
      contentButton1Text: 'Button 1 Text',
      contentButton1Route: 'Button 1 Route',
      contentButton2Text: 'Button 2 Text',
      contentButton2Route: 'Button 2 Route',
      contentSortOrder: 'Sort Order',
      contentRequiredFields: 'Page, Section, and Title are required',
      contentImage: 'Image',
      contentClickUpload: 'Click to upload image',
      contentRemoveImage: 'Remove image',
      uploading: 'Uploading...',
      subtitle: 'Subtitle',
      article: 'Article',
      text: 'Text',
      media: 'Media',
      image: 'Image',
      video: 'Video',
      gallery: 'Gallery',
      // Banners
      banners: 'Banners',
      searchBanners: 'Search banners...',
      noBannersFound: 'No banners found',
      bannerTitle: 'Banner Title',
      bannerPosition: 'Position',
      bannerPreview: 'Preview',
      bannerAdd: 'Add Banner',
      bannerEdit: 'Edit Banner',
      bannerDelete: 'Delete Banner',
      bannerDeleteConfirm: 'Are you sure you want to delete this banner?',
      bannerSubtitle: 'Subtitle',
      bannerImage: 'Banner Image',
      bannerHero: 'Hero',
      bannerSidebar: 'Sidebar',
      bannerFooter: 'Footer',
      bannerSortOrder: 'Sort Order',
      bannerTitleRequired: 'Banner title is required',
      // Posts
      posts: 'Posts',
      searchPosts: 'Search posts...',
      noPostsFound: 'No posts found',
      createPost: 'Create Post',
      editPost: 'Edit Post',
      create_post_placeholder: 'Write something...',
      postTitle: 'Title',
      postType: 'Type',
      postAuthor: 'Author',
      postDate: 'Date',
      postLikes: 'Likes',
      postComments: 'Comments',
      post: 'Post',
      content: 'Content',
      tags: 'Tags',
      addTag: 'Add Tag',
      location: 'Location',
      locationPlaceholder: 'Enter location...',
      privacy: 'Privacy',
      public: 'Public',
      private: 'Private',
      group: 'Group',
      selectGroup: 'Select Group',
      noGroupsMessage: 'No groups available',
      members: 'Members',
      user: 'User',
      officeManager: 'Office Manager',
      lawyer: 'Lawyer',
      mediaUploadFailed: 'Media upload failed',
      confirmDelete: 'Confirm Delete',
      confirmDeleteMsg: 'Are you sure you want to delete this item?',
      // Lawyer management
      addLawyer: 'Add Lawyer',
      searchLawyers: 'Search lawyers...',
      noLawyersFound: 'No lawyers found',
      lawyerDetails: 'Lawyer Details',
      manageLawyer: 'Manage Lawyer',
      viewFullProfile: 'View Full Profile',
      viewDetails: 'View Details',
      specialization: 'Specialization',
      bar_association: 'Bar Association',
      license_number: 'License Number',
      bio: 'Bio',
      removeFromFirm: 'Remove from Firm',
      statusActions: 'Status & Actions',
      selectUser: 'Select User',
      recentCases: 'Recent Cases',
      viewAll: 'View All',
      searchCases: 'Search cases...',
      noCasesFound: 'No cases found',
      cases: {
        title: 'Cases',
        caseTitle: 'Case Title',
        caseType: 'Case Type',
        caseNumber: 'Case Number',
        caseStatus: 'Case Status',
        court: 'Court',
        filedDate: 'Filed Date',
        noTitle: 'Untitled',
        new_case: 'New Case',
        editCase: 'Edit Case',
        deleteCase: 'Delete Case',
        deleteConfirm: 'Are you sure you want to delete this case?',
        deleteError: 'Failed to delete case',
        saveError: 'Failed to save case',
        allStatuses: 'All Statuses',
        description: 'Description',
        value: 'Case Value',
        category: 'Category',
        priority: 'Priority',
        low: 'Low',
        medium: 'Medium',
        high: 'High',
        openDate: 'Open Date',
        deadline: 'Deadline',
        nextHearing: 'Next Hearing',
        days: 'Days',
        year: 'Year',
        startDate: 'Start Date',
        date: 'Date',
        location: 'Location',
        info: 'Case Info',
        plaintiff: 'Plaintiff',
        defendant: 'Defendant',
        otherParties: 'Other Parties',
        partyName: 'Party Name',
        nationalId: 'National ID',
        clientRole: 'Client Role',
        selectLawyer: 'Select Lawyer',
        relatedCases: 'Related Cases',
        totalRelatedCases: 'Total Related Cases',
        noRelatedCasesAvailable: 'No related cases available',
        addParty: 'Add Party',
        // Sessions
        sessions: 'Sessions',
        totalSessions: 'Total Sessions',
        noSessions: 'No sessions found',
        addSession: 'Add Session',
        sessionDate: 'Session Date',
        sessionTime: 'Session Time',
        sessionType: 'Session Type',
        // Actions
        actions: 'Actions',
        addAction: 'Add Action',
        totalActions: 'Total Actions',
        noActionsFound: 'No actions found',
        actionTitle: 'Action Title',
        actionDescription: 'Action Description',
        actionType: 'Action Type',
        actionStatus: 'Action Status',
        actionDate: 'Action Date',
        completed: 'Completed',
        pending: 'Pending',
        // Expenses
        expenses: 'Expenses',
        expense: 'Expense',
        totalExpenses: 'Total Expenses',
        totalExpenseAmount: 'Total Amount',
        noExpenses: 'No expenses found',
        addExpense: 'Add Expense',
        uncategorized: 'Uncategorized',
        unknown: 'Unknown',
        // Payments & Settlements
        payments: 'Payments',
        totalPayments: 'Total Payments',
        totalPaid: 'Total Paid',
        remaining: 'Remaining',
        numberOfPayments: 'Number of Payments',
        noPayments: 'No payments found',
        addPayment: 'Add Payment',
        payment: 'Payment',
        paymentSchedule: 'Payment Schedule',
        amount: 'Amount',
        frequency: 'Frequency',
        intervalDays: 'Interval (Days)',
        method: 'Method',
        paidBy: 'Paid By',
        agreedBy: 'Agreed By',
        downloadReceipt: 'Download Receipt',
        noReceipt: 'No receipt available',
        settlements: 'Settlements',
        totalSettlements: 'Total Settlements',
        noSettlements: 'No settlements found',
        addSettlement: 'Add Settlement',
        settlement: 'Settlement',
        // Documents
        documents: 'Documents',
        noDocs: 'No documents',
      },
      // HR / Employees
      totalEmployees: 'Total Employees',
      allEmployees: 'All Employees',
      searchEmployees: 'Search employees...',
      employee: 'Employee',
      departments: 'Departments',
      editEmployee: 'Edit Employee',
      employeeUpdated: 'Employee updated successfully',
      employeeCreated: 'Employee created successfully',
      employeeDeleted: 'Employee deleted successfully',
      update: 'Update',
      // HR Dashboard
      hrManagement: 'HR Management',
      hrOverview: 'HR overview and statistics',
      attendance: 'Attendance',
      attendanceTracking: 'Track employee attendance',
      leaveRequests: 'Leave Requests',
      leaveManagement: 'Manage employee leaves',
      payroll: 'Payroll',
      payrollManagement: 'Manage payroll and salaries',
      expenses: 'Expenses',
      expensesTracking: 'Track company expenses',
      recruitment: 'Recruitment',
      recruitmentPipeline: 'Recruitment pipeline',
      organization: 'Organization',
      orgStructure: 'Organization structure',
      hrReports: 'HR reports and analytics',
      // Accounting Dashboard
      accounting: 'Accounting',
      accOverview: 'Financial overview and statistics',
      'acc.salesInvoicesDesc': 'Manage sales invoices',
      'acc.purchaseInvoicesDesc': 'Manage purchase invoices',
      'acc.paymentsDesc': 'Track payment entries',
      'acc.journalDesc': 'Journal entries management',
      'acc.customersDesc': 'Manage customers',
      'acc.suppliersDesc': 'Manage suppliers',
      'acc.chartDesc': 'Chart of accounts',
      'acc.costCentersDesc': 'Cost centers management',
      'acc.payrollDesc': 'Payroll management',
      'acc.bankDesc': 'Bank accounts management',
      'acc.taxDesc': 'Tax categories',
      'acc.currenciesDesc': 'Currency management',
      'acc.paymentTermsDesc': 'Payment terms',
      'acc.reportsDesc': 'Financial reports',
      'acc.trialBalanceDesc': 'Trial balance report',
      'acc.cashFlowDesc': 'Cash flow statement',
      'acc.caseFinancialsDesc': 'Case financial reports',
      'acc.organizationDesc': 'Organization settings',
      // Settings
      settings: 'Settings',
      accountSettings: 'Account Settings',
      profile: 'Profile',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      resetPassword: 'Reset Password',
      fullName: 'Full Name',
      saveChanges: 'Save Changes',
      allFieldsRequired: 'All fields are required',
      passwordsDoNotMatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 6 characters',
      passwordChanged: 'Password changed successfully',
      profileUpdated: 'Profile updated successfully',
      updateFailed: 'Update failed',
      changeFailed: 'Operation failed',
      loginCredentials: 'Login Credentials',
      passwordHint: 'Required if email is provided. Employee will use this to log in.',
      // HR Module
      hr: {
        totalEmployees: 'Total Employees',
        presentToday: 'Present Today',
        pendingLeaves: 'Pending Leaves',
        departments: 'Departments',
        totalPayrollCost: 'Total Payroll Cost',
        pendingPayments: 'Pending Payments',
        netPayThisMonth: 'Net Pay This Month',
        salaryPayableBalance: 'Salary Payable Balance',
        pendingLeaveRequests: 'Pending Leave Requests',
        employee: 'Employee',
        leaveType: 'Leave Type',
        fromDate: 'From Date',
        quickActions: 'Quick Actions',
        addEmployee: 'Add Employee',
        recordAttendance: 'Record Attendance',
        newLeaveRequest: 'New Leave Request',
        processPayroll: 'Process Payroll',
        manageOrganization: 'Manage Organization',
        recentAttendance: 'Recent Attendance',
        absent: 'Absent',
        lateArrivals: 'Late Arrivals',
        halfDay: 'Half Day',
        attendanceRecords: 'Attendance Records',
        date: 'Date',
        checkIn: 'Check In',
        checkOut: 'Check Out',
        lateEntry: 'Late Entry',
        late: 'Late',
        onTime: 'On Time',
        selectEmployee: 'Select Employee',
        present: 'Present',
        onLeave: 'On Leave',
        workFromHome: 'Work From Home',
        no: 'No',
        yes: 'Yes',
        fillRequired: 'Please fill required fields',
        attendanceRecorded: 'Attendance recorded successfully',
        confirmDeleteAttendance: 'Are you sure you want to delete this attendance record?',
        attendanceDeleted: 'Attendance deleted successfully',
        addAttendanceRecord: 'Add attendance record',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        totalRequests: 'Total Requests',
        toDate: 'To Date',
        days: 'Days',
        day: 'Day',
        annualLeave: 'Annual Leave',
        sickLeave: 'Sick Leave',
        personalLeave: 'Personal Leave',
        emergencyLeave: 'Emergency Leave',
        totalDays: 'Total Days',
        description: 'Description',
        leaveReason: 'Leave reason',
        submitRequest: 'Submit Request',
        leaveCreated: 'Leave request created successfully',
        confirmApprove: 'Approve leave for {name}?',
        confirmReject: 'Reject leave for {name}?',
        leaveApproved: 'Leave approved successfully',
        leaveRejected: 'Leave rejected successfully',
        submitLeaveRequest: 'Submit leave request',
        approve: 'Approve',
        reject: 'Reject',
        totalPayroll: 'Total Payroll',
        submitted: 'Submitted',
        draft: 'Draft',
        totalNetPay: 'Total Net Pay',
        addPayroll: 'Add Payroll',
        payrollRecords: 'Payroll Records',
        postingDate: 'Posting Date',
        period: 'Period',
        basicSalary: 'Basic Salary',
        allowances: 'Allowances',
        deductions: 'Deductions',
        netPay: 'Net Pay',
        employeeName: 'Employee Name',
        startDate: 'Start Date',
        endDate: 'End Date',
        housingAllowance: 'Housing Allowance',
        transportAllowance: 'Transport Allowance',
        totalDeduction: 'Total Deduction',
        createPayroll: 'Create Payroll',
        createNewPayroll: 'Create new payroll entry',
        payrollCreated: 'Payroll created successfully',
        confirmSubmitPayroll: 'Submit payroll for {name}?',
        payrollSubmitted: 'Payroll submitted successfully',
        syncAccounting: 'Sync to Accounting',
        syncedToAccounting: 'Synced to accounting successfully',
        payrollDetails: 'Payroll Details',
        submit: 'Submit',
        totalClaims: 'Total Claims',
        totalAmount: 'Total Amount',
        addExpenseClaim: 'Add Expense Claim',
        expenseClaims: 'Expense Claims',
        claimedAmount: 'Claimed Amount',
        sanctionedAmount: 'Sanctioned Amount',
        createNewExpense: 'Create new expense claim',
        createClaim: 'Create Claim',
        expenseDetails: 'Expense Details',
        reimbursed: 'Reimbursed',
        expenseCreated: 'Expense created successfully',
        confirmApproveExpense: 'Approve expense for {name}?',
        expenseApproved: 'Expense approved successfully',
        jobOpenings: 'Job Openings',
        totalApplicants: 'Total Applicants',
        trainingEvents: 'Training Events',
        holidays: 'Holidays',
        addJobOpening: 'Add Job Opening',
        jobTitle: 'Job Title',
        department: 'Department',
        postedOn: 'Posted On',
        applicants: 'Applicants',
        addApplicant: 'Add Applicant',
        applicantName: 'Applicant Name',
        email: 'Email',
        jobCreated: 'Job opening created successfully',
        applicantCreated: 'Applicant created successfully',
        addDepartment: 'Add Department',
        deptCode: 'Department Code',
        deptName: 'Department Name',
        headOfDept: 'Head of Department',
        notAssigned: 'Not Assigned',
        disabled: 'Disabled',
        active: 'Active',
        designations: 'Designations',
        addDesignation: 'Add Designation',
        designationName: 'Designation Name',
        grades: 'Grades',
        addGrade: 'Add Grade',
        gradeName: 'Grade Name',
        companies: 'Companies',
        addCompany: 'Add Company',
        companyName: 'Company Name',
        editDepartment: 'Edit Department',
        deptUpdated: 'Department updated successfully',
        deptCreated: 'Department created successfully',
        deptDeleted: 'Department deleted successfully',
        confirmDelete: 'Are you sure you want to delete this?',
        editDesignation: 'Edit Designation',
        desigCreated: 'Designation created successfully',
        desigUpdated: 'Designation updated successfully',
        desigDeleted: 'Designation deleted successfully',
        editGrade: 'Edit Grade',
        gradeCreated: 'Grade created successfully',
        gradeUpdated: 'Grade updated successfully',
        gradeDeleted: 'Grade deleted successfully',
        editCompany: 'Edit Company',
        companyCreated: 'Company created successfully',
        companyUpdated: 'Company updated successfully',
        companyDeleted: 'Company deleted successfully',
        reportEmployeeReport: 'Employee Report',
        reportEmployeeDesc: 'Complete employee list with details',
        reportAttendanceReport: 'Attendance Report',
        reportAttendanceDesc: 'Attendance summary and details',
        reportLeaveReport: 'Leave Report',
        reportLeaveDesc: 'Leave balance and history',
        reportPayrollReport: 'Payroll Report',
        reportPayrollDesc: 'Payroll summary and details',
        reportExpenseReport: 'Expense Report',
        reportExpenseDesc: 'Expense claims summary',
        reportRecruitmentReport: 'Recruitment Report',
        reportRecruitmentDesc: 'Job applicants and status',
        reportSalaryRegister: 'Salary Register',
        reportSalaryRegisterDesc: 'Complete salary register',
        reportIncomeTax: 'Income Tax Computation',
        reportIncomeTaxDesc: 'Tax computation details',
        reportPaymentMode: 'Payment Mode Analysis',
        reportPaymentModeDesc: 'Payment by mode breakdown',
        employeeId: 'Employee ID',
        designation: 'Designation',
        hrSettings: 'HR Settings',
        settingsDesc: 'Configure HR module settings',
        generalSettings: 'General Settings',
        defaultCompany: 'Default Company',
        fiscalYear: 'Fiscal Year',
        country: 'Country',
        currency: 'Currency',
        saudiArabia: 'Saudi Arabia',
        jordan: 'Jordan',
        uae: 'UAE',
        other: 'Other',
        attendanceSettings: 'Attendance Settings',
        workingHours: 'Working Hours',
        lateThreshold: 'Late Threshold (Minutes)',
        leaveSettings: 'Leave Settings',
        annualLeaveDays: 'Annual Leave Days',
        sickLeaveDays: 'Sick Leave Days',
        settingsSaved: 'Settings saved successfully',
      },
      // Accounting Module
      acc: {
        salesInvoices: 'Sales Invoices', purchaseInvoices: 'Purchase Invoices', paymentEntries: 'Payment Entries',
        journalEntries: 'Journal Entries', customers: 'Customers', suppliers: 'Suppliers',
        chartOfAccounts: 'Chart of Accounts', costCenters: 'Cost Centers', payroll: 'Payroll',
        bankAccounts: 'Bank Accounts', taxCategories: 'Tax Categories', currencies: 'Currencies',
        paymentTerms: 'Payment Terms', reports: 'Reports', trialBalance: 'Trial Balance',
        cashFlow: 'Cash Flow', caseFinancials: 'Case Financials', organization: 'Organization',
        accountsReceivable: 'Accounts Receivable', accountsPayable: 'Accounts Payable',
        balanceSheet: 'Balance Sheet', profitAndLoss: 'Profit & Loss',
        recentInvoices: 'Recent Invoices', quickActions: 'Quick Actions',
        newSalesInvoice: 'New Sales Invoice', newPurchaseInvoice: 'New Purchase Invoice',
        recordPayment: 'Record Payment', newJournalEntry: 'New Journal Entry',
        manageCustomers: 'Manage Customers', viewReports: 'View Reports',
        topCustomers: 'Top Customers', topSuppliers: 'Top Suppliers',
        totalSales: 'Total Sales', totalPayments: 'Total Payments',
        totalPurchases: 'Total Purchases', outstandingAmount: 'Outstanding Amount',
        customer: 'Customer', supplier: 'Supplier', date: 'Date', amount: 'Amount',
        status: 'Status', invoiceNo: 'Invoice No.', invoice: 'Invoice',
        account: 'Account', accountName: 'Account Name', accountNumber: 'Account Number',
        debit: 'Debit', credit: 'Credit', total: 'Total',
        generate: 'Generate', generateReport: 'Generate Report',
        download: 'Download', noData: 'No data available',
        name: 'Name', type: 'Type', description: 'Description', actions: 'Actions',
        edit: 'Edit', delete: 'Delete', save: 'Save', cancel: 'Cancel',
        add: 'Add', create: 'Create', active: 'Active', disabled: 'Disabled',
        yes: 'Yes', no: 'No', none: 'None', pending: 'Pending', submitted: 'Submitted',
        allAccountTypes: 'All Account Types', allStatus: 'All Status', allTypes: 'All Types',
        category: 'Category', inflow: 'Inflow', outflow: 'Outflow', net: 'Net',
        dueDate: 'Due Date', outstanding: 'Outstanding', age: 'Age',
        bankName: 'Bank Name', balance: 'Balance', currency: 'Currency', symbol: 'Symbol',
        precision: 'Precision', currencyCode: 'Currency Code', currencyName: 'Currency Name',
        addBankAccount: 'Add Bank Account', addCurrency: 'Add Currency',
        addCostCenter: 'Add Cost Center', editCostCenter: 'Edit Cost Center',
        parentCostCenter: 'Parent Cost Center', isGroup: 'Is Group', manager: 'Manager',
        addPaymentTerm: 'Add Payment Term', termName: 'Term Name', dueDays: 'Due Days',
        addTaxCategory: 'Add Tax Category', categoryName: 'Category Name',
        addTaxRule: 'Add Tax Rule', taxRate: 'Tax Rate', taxCategory: 'Tax Category',
        addTaxTemplate: 'Add Tax Template', templateName: 'Template Name',
        addWithholding: 'Add Withholding', withholdingName: 'Withholding Name',
        withholdingRate: 'Withholding Rate', threshold: 'Threshold',
        addUser: 'Add User', userName: 'User Name', email: 'Email', password: 'Password',
        roles: 'Roles', roleName: 'Role Name', permissions: 'Permissions',
        addRole: 'Add Role', addShop: 'Add Shop', shopName: 'Shop Name',
        addSubscription: 'Add Subscription', plan: 'Plan', monthlyAmount: 'Monthly Amount',
        nextBilling: 'Next Billing', frequency: 'Frequency',
        addRecurring: 'Add Reccurring', startDate: 'Start Date', endDate: 'End Date',
        addDeferredRevenue: 'Add Deferred Revenue', recognized: 'Recognized', remaining: 'Remaining',
        addExchangeRate: 'Add Exchange Rate', fromCurrency: 'From Currency', toCurrency: 'To Currency',
        newRate: 'New Rate', oldRate: 'Old Rate', gainLoss: 'Gain/Loss',
        addFiscalYear: 'Add Fiscal Year', yearName: 'Year Name', year: 'Year', month: 'Month',
        accountingPeriods: 'Accounting Periods', reopen: 'Reopen',
        addInvoiceDiscounting: 'Add Invoice Discounting', invoiceAmount: 'Invoice Amount',
        addAssignment: 'Add Assignment', party: 'Party', paymentType: 'Payment Type',
        paidFrom: 'Paid From', paidTo: 'Paid To', reference: 'Reference',
        addItem: 'Add Item', itemCode: 'Item Code', itemGroup: 'Item Group',
        available: 'Available', rate: 'Rate', qty: 'Qty',
        addSalary: 'Add Salary', salaryComponents: 'Salary Components',
        salaryStructures: 'Salary Structures', salarySlips: 'Salary Slips',
        payrollEntries: 'Payroll Entries', newPayrollEntry: 'New Payroll Entry',
        salaryStructure: 'Salary Structure', refNo: 'Ref No.', postingDate: 'Posting Date',
        grossPay: 'Gross Pay', netPay: 'Net Pay', period: 'Period',
        calculationType: 'Calculation Type', effectiveFrom: 'Effective From',
        company: 'Company', abbreviation: 'Abbreviation', country: 'Country',
        parentDepartment: 'Parent Department', departments: 'Departments', branches: 'Branches',
        designations: 'Designations', setupWizard: 'Setup Wizard', completed: 'Completed',
        newEntry: 'New Entry', newCustomer: 'New Customer', newSupplier: 'New Supplier',
        address: 'Address', phone: 'Phone', taxId: 'Tax ID', supplierType: 'Supplier Type',
        searchInvoices: 'Search invoices...', searchPayments: 'Search payments...',
        selectCase: 'Select Case', selectCaseToView: 'Select a case to view financial details',
        linkedCase: 'Linked Case', noCaseLink: 'No case linked',
        bankReconciliation: 'Bank Reconciliation', matchTransactions: 'Match Transactions',
        matched: 'Matched', unmatched: 'Unmatched', matchedWith: 'Matched With',
        reconciliation: 'Reconciliation', bankStatement: 'Bank Statement',
        periodClosure: 'Period Closure', closePeriod: 'Close Period',
        auditTrail: 'Audit Trail', timestamp: 'Timestamp', user: 'User', action: 'Action',
        consolidation: 'Consolidation', runConsolidation: 'Run Consolidation',
        variance: 'Variance', utilization: 'Utilization',
        incomeTaxSlabs: 'Income Tax Slabs', addSlab: 'Add Slab', taxSlabName: 'Tax Slab Name',
        hrSyncLog: 'HR Sync Log', syncNow: 'Sync Now', syncStatus: 'Sync Status',
        pendingSync: 'Pending Sync', noSyncLogs: 'No sync logs available',
        totalInvoices: 'Total Invoices', totalExpenses: 'Total Expenses',
        totalCollections: 'Total Collections', totalDebit: 'Total Debit', totalCredit: 'Total Credit',
        netBalance: 'Net Balance', netAmount: 'Net Amount', totalAmount: 'Total Amount',
        overdueInvoices: 'Overdue Invoices', open: 'Open', settled: 'Settled', stopped: 'Stopped',
        never: 'Never', nextDate: 'Next Date', lastRun: 'Last Run',
        sendLetter: 'Send Letter', withdraw: 'Withdraw', withdrawal: 'Withdrawal',
        mrr: 'MRR', arDesc: 'Aging analysis of receivables', apDesc: 'Aging analysis of payables',
        trialBalanceDesc: 'Account balances summary', balanceSheetDesc: 'Financial position report',
        profitLossDesc: 'Income and expenses summary', journalEntriesDesc: 'Double-entry journal',
        viewAll: 'View All', priority: 'Priority',
        inactive: 'Inactive', all: 'All', from: 'From', to: 'To',
        fromDate: 'From Date', toDate: 'To Date', actual: 'Actual', budget: 'Budget',
        budgetVsActual: 'Budget vs Actual', fromIncome: 'From Income', toIncome: 'To Income',
        addLine: 'Add Line', items: 'Items', accounts: 'Accounts',
        salesInvoicesDesc: 'Manage sales invoices', purchaseInvoicesDesc: 'Manage purchase invoices',
        paymentsDesc: 'Track payment entries', journalDesc: 'Journal entries management',
        customersDesc: 'Manage customers', suppliersDesc: 'Manage suppliers',
        chartDesc: 'Chart of accounts', costCentersDesc: 'Cost centers management',
        payrollDesc: 'Payroll management', bankDesc: 'Bank accounts management',
        taxDesc: 'Tax categories', currenciesDesc: 'Currency management',
        paymentTermsDesc: 'Payment terms', reportsDesc: 'Financial reports',
      },
    },
    ar: {
      home: 'الرئيسية',
      about: 'عن المكتب',
      lawyers: { title: 'المحامون' },
      practices: 'مجالات الممارسة',
      reviews: 'التقييمات',
      contact: 'اتصل بنا',
      // Office dashboard
      officeManagement: 'إدارة المكتب',
      overview: 'نظرة عامة',
      statsAndIncome: 'الإحصائيات والإيرادات',
      filter: 'تصفية',
      add: 'إضافة',
      save: 'حفظ',
      saving: 'جاري الحفظ...',
      cancel: 'إلغاء',
      delete: 'حذف',
      edit: 'تعديل',
      view: 'عرض',
      create: 'إنشاء',
      refresh: 'تحديث',
      remove: 'إزالة',
      manage: 'إدارة',
      select: 'اختر',
      required: 'مطلوب',
      optional: 'اختياري',
      loading: 'جاري التحميل...',
      noData: 'لا توجد بيانات',
      showing: 'عرض',
      of: 'من',
      search: 'بحث',
      actions: 'الإجراءات',
      status: 'الحالة',
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      address: 'العنوان',
      description: 'الوصف',
      notes: 'ملاحظات',
      type: 'النوع',
      amount: 'المبلغ',
      active: 'نشط',
      inactive: 'غير نشط',
      open: 'مفتوح',
      closed: 'مغلق',
      settled: 'تم التسوية',
      inProgress: 'قيد المعالجة',
      draft: 'مسودة',
      published: 'منشور',
      live: 'مباشر',
      publish: 'نشر',
      activate: 'تفعيل',
      deactivate: 'تعطيل',
      // Clients
      clients: 'العملاء',
      client: 'عميل',
      recentClients: 'أحدث العملاء',
      searchClients: 'البحث عن عملاء...',
      noClientsFound: 'لا يوجد عملاء',
      addClient: 'إضافة عميل',
      editClient: 'تعديل العميل',
      firstName: 'الاسم الأول',
      fatherName: 'اسم الأب',
      thirdName: 'الاسم الثالث',
      familyName: 'الاسم الأخير',
      nationality: 'الجنسية',
      person: 'شخص',
      company: 'شركة',
      companyName: 'اسم الشركة',
      contactPerson: 'شخص الاتصال',
      clientAccount: 'حساب العميل',
      clientAccountHint: 'إنشاء بيانات تسجيل دخول لهذا العميل',
      username: 'اسم المستخدم',
      password: 'كلمة المرور',
      confirm_password: 'تأكيد كلمة المرور',
      password_min_length: 'يجب أن تكون كلمة المرور 6 أحرف على الأقل',
      passwords_do_not_match: 'كلمتا المرور غير متطابقتين',
      fillAllAccountFields: 'يرجى ملء جميع حقول الحساب',
      // Staff
      staff: 'الموظفون',
      searchStaff: 'البحث عن موظفين...',
      noStaffFound: 'لا يوجد موظفون',
      addStaff: 'إضافة موظف',
      addEmployee: 'إضافة موظف',
      fillEmployeeDetails: 'أدخل تفاصيل الموظف',
      personalInfo: 'المعلومات الشخصية',
      middleName: 'الاسم الأوسط',
      lastName: 'اللقب',
      gender: 'الجنس',
      male: 'ذكر',
      female: 'أنثى',
      dateOfBirth: 'تاريخ الميلاد',
      nationalId: 'الرقم الوطني',
      contactInfo: 'معلومات الاتصال',
      cellNumber: 'رقم الجوال',
      personalEmail: 'البريد الشخصي',
      employmentDetails: 'تفاصيل التوظيف',
      employeeId: 'الرقم الوظيفي',
      autoGenerated: 'يتم توليده تلقائياً',
      designation: 'المسمى الوظيفي',
      dateOfJoining: 'تاريخ المباشرة',
      grade: 'الدرجة',
      contractType: 'نوع العقد',
      fullTime: 'دوام كامل',
      partTime: 'دوام جزئي',
      contract: 'عقد',
      temporary: 'مؤقت',
      onLeave: 'في إجازة',
      terminated: 'منتهي الخدمة',
      country: 'الدولة',
      workLocation: 'موقع العمل',
      salaryInfo: 'معلومات الراتب',
      basicSalary: 'الراتب الأساسي',
      salary: 'الراتب',
      salaryCurrency: 'العملة',
      salaryMode: 'طريقة الراتب',
      bankName: 'اسم البنك',
      bankAccountNo: 'رقم الحساب البنكي',
      department: 'القسم',
      searchByName: 'البحث بالاسم...',
      allStatuses: 'جميع الحالات',
      role: 'الوظيفة',
      selectRole: 'اختر الوظيفة',
      hireDate: 'تاريخ التوظيف',
      failedToAddStaff: 'فشل في إضافة الموظف',
      // Appointments
      appointments: {
        title: 'المواعيد',
        date: 'التاريخ',
        time: 'الوقت',
        location: 'الموقع',
        scheduled: 'مجدول',
      },
      searchAppointments: 'البحث عن مواعيد...',
      noAppointmentsFound: 'لا توجد مواعيد',
      upcomingAppointments: 'المواعيد القادمة',
      // Courses
      courses: 'الدورات',
      searchCourses: 'البحث عن دورات...',
      noCoursesFound: 'لا توجد دورات',
      noCoursesYet: 'لا توجد دورات بعد',
      addCourse: 'إضافة دورة',
      addFirstCourse: 'إضافة أول دورة',
      editCourse: 'تعديل الدورة',
      courseTitle: 'عنوان الدورة',
      duration: 'المدة',
      price: 'السعر',
      discount: 'الخصم',
      discountPercent: 'نسبة الخصم',
      free: 'مجاني',
      freeCourse: 'دورة مجانية',
      enrollments: 'التسجيلات',
      currency: 'العملة',
      // Resources
      resources: { title: 'الموارد' },
      searchResources: 'البحث عن موارد...',
      noResourcesFound: 'لا توجد موارد',
      resourceType: 'النوع',
      resourceSize: 'الحجم',
      resourceUploadDate: 'تاريخ الرفع',
      // Reports
      reports: 'التقارير',
      searchReports: 'البحث عن تقارير...',
      noReportsFound: 'لا توجد تقارير',
      reportType: 'نوع التقرير',
      reportPeriod: 'الفترة',
      reportGenerated: 'تاريخ الإنشاء',
      revenue: 'الإيرادات',
      avgRevenue: 'متوسط الإيرادات',
      totalIncome: 'إجمالي الإيرادات',
      // Transactions
      transactions: 'المعاملات',
      searchTransactions: 'البحث عن معاملات...',
      noTransactionsFound: 'لا توجد معاملات',
      txIncome: 'الإيرادات',
      txExpenses: 'المصروفات',
      txNet: 'الصافي',
      txAllTypes: 'جميع الأنواع',
      txDescription: 'الوصف',
      txType: 'النوع',
      txCategory: 'الفئة',
      txDate: 'التاريخ',
      // Content
      contentManagement: 'المحتوى',
      searchContent: 'البحث عن محتوى...',
      noContentFound: 'لا يوجد محتوى',
      contentPage: 'الصفحة',
      contentSection: 'القسم',
      contentRequired: 'المحتوى مطلوب',
      contentLastUpdated: 'آخر تحديث',
      contentAdd: 'إضافة محتوى',
      contentEdit: 'تعديل المحتوى',
      contentDelete: 'حذف المحتوى',
      contentDeleteConfirm: 'هل أنت متأكد من حذف "{title}"؟',
      contentTitle: 'العنوان',
      contentSubtitle: 'العنوان الفرعي',
      contentBody: 'المحتوى',
      contentImageUrl: 'رابط الصورة',
      contentButton1Text: 'نص الزر 1',
      contentButton1Route: 'رابط الزر 1',
      contentButton2Text: 'نص الزر 2',
      contentButton2Route: 'رابط الزر 2',
      contentSortOrder: 'ترتيب العرض',
      contentRequiredFields: 'الصفحة والقسم والعنوان حقول مطلوبة',
      contentImage: 'الصورة',
      contentClickUpload: 'اضغط لرفع صورة',
      contentRemoveImage: 'إزالة الصورة',
      uploading: 'جاري الرفع...',
      subtitle: 'العنوان الفرعي',
      article: 'مقال',
      text: 'نص',
      media: 'وسائط',
      image: 'صورة',
      video: 'فيديو',
      gallery: 'معرض الصور',
      // Banners
      banners: 'البانرات',
      searchBanners: 'البحث عن بانرات...',
      noBannersFound: 'لا توجد بانرات',
      bannerTitle: 'عنوان البانر',
      bannerPosition: 'الموضع',
      bannerPreview: 'معاينة',
      bannerAdd: 'إضافة بانر',
      bannerEdit: 'تعديل البانر',
      bannerDelete: 'حذف البانر',
      bannerDeleteConfirm: 'هل أنت متأكد من حذف هذا البانر؟',
      bannerSubtitle: 'العنوان الفرعي',
      bannerImage: 'صورة البانر',
      bannerHero: 'رئيسي',
      bannerSidebar: 'شريط جانبي',
      bannerFooter: 'تذييل',
      bannerSortOrder: 'ترتيب العرض',
      bannerTitleRequired: 'عنوان البانر مطلوب',
      // Posts
      posts: 'المنشورات',
      searchPosts: 'البحث عن منشورات...',
      noPostsFound: 'لا توجد منشورات',
      createPost: 'إنشاء منشور',
      editPost: 'تعديل المنشور',
      create_post_placeholder: 'اكتب شيئاً...',
      postTitle: 'العنوان',
      postType: 'النوع',
      postAuthor: 'الكاتب',
      postDate: 'التاريخ',
      postLikes: 'الإعجابات',
      postComments: 'التعليقات',
      post: 'منشور',
      content: 'المحتوى',
      tags: 'الوسوم',
      addTag: 'إضافة وسم',
      location: 'الموقع',
      locationPlaceholder: 'أدخل الموقع...',
      privacy: 'الخصوصية',
      public: 'عام',
      private: 'خاص',
      group: 'مجموعة',
      selectGroup: 'اختر مجموعة',
      noGroupsMessage: 'لا توجد مجموعات',
      members: 'الأعضاء',
      user: 'مستخدم',
      officeManager: 'مدير المكتب',
      lawyer: 'محامٍ',
      mediaUploadFailed: 'فشل رفع الوسائط',
      confirmDelete: 'تأكيد الحذف',
      confirmDeleteMsg: 'هل أنت متأكد من حذف هذا العنصر؟',
      // Lawyer management
      addLawyer: 'إضافة محامٍ',
      searchLawyers: 'البحث عن محامين...',
      noLawyersFound: 'لا يوجد محامون',
      lawyerDetails: 'تفاصيل المحامي',
      manageLawyer: 'إدارة المحامي',
      viewFullProfile: 'عرض الملف الكامل',
      viewDetails: 'عرض التفاصيل',
      specialization: 'التخصص',
      bar_association: 'نقابة المحامين',
      license_number: 'رقم الترخيص',
      bio: 'نبذة',
      removeFromFirm: 'إزالة من المكتب',
      statusActions: 'الحالة والإجراءات',
      selectUser: 'اختر مستخدم',
      recentCases: 'أحدث القضايا',
      viewAll: 'عرض الكل',
      searchCases: 'البحث عن قضايا...',
      noCasesFound: 'لا توجد قضايا',
      cases: {
        title: 'القضايا',
        caseTitle: 'عنوان القضية',
        caseType: 'نوع القضية',
        caseNumber: 'رقم القضية',
        caseStatus: 'حالة القضية',
        court: 'المحكمة',
        filedDate: 'تاريخ الرفع',
        noTitle: 'بدون عنوان',
        new_case: 'قضية جديدة',
        editCase: 'تعديل القضية',
        deleteCase: 'حذف القضية',
        deleteConfirm: 'هل أنت متأكد من حذف هذه القضية؟',
        deleteError: 'فشل حذف القضية',
        saveError: 'فشل حفظ القضية',
        allStatuses: 'جميع الحالات',
        description: 'الوصف',
        value: 'قيمة القضية',
        category: 'الفئة',
        priority: 'الأولوية',
        low: 'منخفضة',
        medium: 'متوسطة',
        high: 'عالية',
        openDate: 'تاريخ الفتح',
        deadline: 'الموعد النهائي',
        nextHearing: 'الجلسة القادمة',
        days: 'أيام',
        year: 'سنة',
        startDate: 'تاريخ البدء',
        date: 'التاريخ',
        location: 'الموقع',
        info: 'معلومات القضية',
        plaintiff: 'المدعي',
        defendant: 'المدعى عليه',
        otherParties: 'أطراف أخرى',
        partyName: 'اسم الطرف',
        nationalId: 'الرقم الوطني',
        clientRole: 'دور العميل',
        selectLawyer: 'اختر محامٍ',
        relatedCases: 'القضايا ذات الصلة',
        totalRelatedCases: 'إجمالي القضايا ذات الصلة',
        noRelatedCasesAvailable: 'لا توجد قضايا ذات صلة',
        addParty: 'إضافة طرف',
        // Sessions
        sessions: 'الجلسات',
        totalSessions: 'إجمالي الجلسات',
        noSessions: 'لا توجد جلسات',
        addSession: 'إضافة جلسة',
        sessionDate: 'تاريخ الجلسة',
        sessionTime: 'وقت الجلسة',
        sessionType: 'نوع الجلسة',
        // Actions
        actions: 'الإجراءات',
        addAction: 'إضافة إجراء',
        totalActions: 'إجمالي الإجراءات',
        noActionsFound: 'لا توجد إجراءات',
        actionTitle: 'عنوان الإجراء',
        actionDescription: 'وصف الإجراء',
        actionType: 'نوع الإجراء',
        actionStatus: 'حالة الإجراء',
        actionDate: 'تاريخ الإجراء',
        completed: 'مكتمل',
        pending: 'قيد الانتظار',
        // Expenses
        expenses: 'المصروفات',
        expense: 'مصروف',
        totalExpenses: 'إجمالي المصروفات',
        totalExpenseAmount: 'المبلغ الإجمالي',
        noExpenses: 'لا توجد مصروفات',
        addExpense: 'إضافة مصروف',
        uncategorized: 'غير مصنف',
        unknown: 'غير معروف',
        // Payments & Settlements
        payments: 'المدفوعات',
        totalPayments: 'إجمالي المدفوعات',
        totalPaid: 'إجمالي المدفوع',
        remaining: 'المتبقي',
        numberOfPayments: 'عدد الدفعات',
        noPayments: 'لا توجد مدفوعات',
        addPayment: 'إضافة دفعة',
        payment: 'دفعة',
        paymentSchedule: 'جدول الدفع',
        amount: 'المبلغ',
        frequency: 'التكرار',
        intervalDays: 'الفترة (أيام)',
        method: 'الطريقة',
        paidBy: 'مدفوع بواسطة',
        agreedBy: 'تمت الموافقة بواسطة',
        downloadReceipt: 'تحميل الإيصال',
        noReceipt: 'لا يوجد إيصال',
        settlements: 'التسويات',
        totalSettlements: 'إجمالي التسويات',
        noSettlements: 'لا توجد تسويات',
        addSettlement: 'إضافة تسوية',
        settlement: 'تسوية',
        // Documents
        documents: 'المستندات',
        noDocs: 'لا توجد مستندات',
      },
      // HR / Employees
      totalEmployees: 'إجمالي الموظفين',
      allEmployees: 'جميع الموظفين',
      searchEmployees: 'البحث عن موظفين...',
      employee: 'الموظف',
      departments: 'الأقسام',
      editEmployee: 'تعديل الموظف',
      employeeUpdated: 'تم تحديث الموظف بنجاح',
      employeeCreated: 'تم إنشاء الموظف بنجاح',
      employeeDeleted: 'تم حذف الموظف بنجاح',
      update: 'تحديث',
      // HR Dashboard
      hrManagement: 'إدارة الموارد البشرية',
      hrOverview: 'نظرة عامة على الموارد البشرية والإحصائيات',
      attendance: 'الحضور',
      attendanceTracking: 'تتبع حضور الموظفين',
      leaveRequests: 'طلبات الإجازة',
      leaveManagement: 'إدارة إجازات الموظفين',
      payroll: 'الرواتب',
      payrollManagement: 'إدارة الرواتب والأجور',
      expenses: 'المصروفات',
      expensesTracking: 'تتبع مصروفات الشركة',
      recruitment: 'التوظيف',
      recruitmentPipeline: 'مسار التوظيف',
      organization: 'الهيكل التنظيمي',
      orgStructure: 'الهيكل التنظيمي للشركة',
      hrReports: 'تقارير وتحليلات الموارد البشرية',
      // Accounting Dashboard
      accounting: 'المحاسبة',
      accOverview: 'نظرة عامة مالية وإحصائيات',
      'acc.salesInvoicesDesc': 'إدارة فواتير المبيعات',
      'acc.purchaseInvoicesDesc': 'إدارة فواتير الشراء',
      'acc.paymentsDesc': 'تتبع سندات القبض والصرف',
      'acc.journalDesc': 'إدارة القيود المحاسبية',
      'acc.customersDesc': 'إدارة العملاء',
      'acc.suppliersDesc': 'إدارة الموردين',
      'acc.chartDesc': 'دليل الحسابات',
      'acc.costCentersDesc': 'إدارة مراكز التكلفة',
      'acc.payrollDesc': 'إدارة الرواتب',
      'acc.bankDesc': 'إدارة الحسابات البنكية',
      'acc.taxDesc': 'فئات الضرائب',
      'acc.currenciesDesc': 'إدارة العملات',
      'acc.paymentTermsDesc': 'شروط الدفع',
      'acc.reportsDesc': 'التقارير المالية',
      'acc.trialBalanceDesc': 'تقرير ميزان المراجعة',
      'acc.cashFlowDesc': 'قائمة التدفقات النقدية',
      'acc.caseFinancialsDesc': 'التقارير المالية للقضايا',
      'acc.organizationDesc': 'إعدادات المؤسسة',
      // Settings
      settings: 'الإعدادات',
      accountSettings: 'إعدادات الحساب',
      profile: 'الملف الشخصي',
      changePassword: 'تغيير كلمة المرور',
      currentPassword: 'كلمة المرور الحالية',
      newPassword: 'كلمة المرور الجديدة',
      confirmPassword: 'تأكيد كلمة المرور',
      resetPassword: 'إعادة تعيين كلمة المرور',
      fullName: 'الاسم الكامل',
      saveChanges: 'حفظ التغييرات',
      allFieldsRequired: 'جميع الحقول مطلوبة',
      passwordsDoNotMatch: 'كلمات المرور غير متطابقة',
      passwordTooShort: 'يجب أن تكون كلمة المرور 6 أحرف على الأقل',
      passwordChanged: 'تم تغيير كلمة المرور بنجاح',
      profileUpdated: 'تم تحديث الملف الشخصي بنجاح',
      updateFailed: 'فشل التحديث',
      changeFailed: 'فشلت العملية',
      loginCredentials: 'بيانات تسجيل الدخول',
      passwordHint: 'مطلوبة إذا تم تقديم البريد الإلكتروني. سيستخدمها الموظف لتسجيل الدخول.',
      // HR Module
      hr: {
        totalEmployees: 'إجمالي الموظفين',
        presentToday: 'الحاضرون اليوم',
        pendingLeaves: 'الإجازات المعلقة',
        departments: 'الأقسام',
        totalPayrollCost: 'إجمالي تكاليف الرواتب',
        pendingPayments: 'المدفوعات المعلقة',
        netPayThisMonth: 'صافي رواتب هذا الشهر',
        salaryPayableBalance: 'رصيد الرواتب المستحقة',
        pendingLeaveRequests: 'طلبات الإجازة المعلقة',
        employee: 'الموظف',
        leaveType: 'نوع الإجازة',
        fromDate: 'من تاريخ',
        quickActions: 'إجراءات سريعة',
        addEmployee: 'إضافة موظف',
        recordAttendance: 'تسجيل الحضور',
        newLeaveRequest: 'طلب إجازة جديد',
        processPayroll: 'معالجة الرواتب',
        manageOrganization: 'إدارة الهيكل التنظيمي',
        recentAttendance: 'الحضور الأخير',
        absent: 'غائب',
        lateArrivals: 'المتأخرون',
        halfDay: 'نصف يوم',
        attendanceRecords: 'سجلات الحضور',
        date: 'التاريخ',
        checkIn: 'وقت الدخول',
        checkOut: 'وقت الخروج',
        lateEntry: 'الدخول المتأخر',
        late: 'متأخر',
        onTime: 'في الوقت',
        selectEmployee: 'اختر الموظف',
        present: 'حاضر',
        onLeave: 'في إجازة',
        workFromHome: 'العمل من المنزل',
        no: 'لا',
        yes: 'نعم',
        fillRequired: 'يرجى ملء الحقول المطلوبة',
        attendanceRecorded: 'تم تسجيل الحضور بنجاح',
        confirmDeleteAttendance: 'هل أنت متأكد من حذف سجل الحضور هذا؟',
        attendanceDeleted: 'تم حذف سجل الحضور بنجاح',
        addAttendanceRecord: 'إضافة سجل حضور',
        pending: 'معلق',
        approved: 'موافق عليه',
        rejected: 'مرفوض',
        totalRequests: 'إجمالي الطلبات',
        toDate: 'إلى تاريخ',
        days: 'أيام',
        day: 'يوم',
        annualLeave: 'إجازة سنوية',
        sickLeave: 'إجازة مرضية',
        personalLeave: 'إجازة شخصية',
        emergencyLeave: 'إجازة طارئة',
        totalDays: 'إجمالي الأيام',
        description: 'الوصف',
        leaveReason: 'سبب الإجازة',
        submitRequest: 'إرسال الطلب',
        leaveCreated: 'تم إنشاء طلب الإجازة بنجاح',
        confirmApprove: 'الموافقة على إجازة {name}؟',
        confirmReject: 'رفض إجازة {name}؟',
        leaveApproved: 'تم الموافقة على الإجازة بنجاح',
        leaveRejected: 'تم رفض الإجازة بنجاح',
        submitLeaveRequest: 'إرسال طلب الإجازة',
        approve: 'موافقة',
        reject: 'رفض',
        totalPayroll: 'إجمالي الرواتب',
        submitted: 'مرسل',
        draft: 'مسودة',
        totalNetPay: 'إجمالي صافي الرواتب',
        addPayroll: 'إضافة راتب',
        payrollRecords: 'سجلات الرواتب',
        postingDate: 'تاريخ الترحيل',
        period: 'الفترة',
        basicSalary: 'الراتب الأساسي',
        allowances: 'البدلات',
        deductions: 'الاستقطاعات',
        netPay: 'صافي الراتب',
        employeeName: 'اسم الموظف',
        startDate: 'تاريخ البدء',
        endDate: 'تاريخ الانتهاء',
        housingAllowance: 'بدل السكن',
        transportAllowance: 'بدل المواصلات',
        totalDeduction: 'إجمالي الاستقطاعات',
        createPayroll: 'إنشاء راتب',
        createNewPayroll: 'إنشاء سجل راتب جديد',
        payrollCreated: 'تم إنشاء الراتب بنجاح',
        confirmSubmitPayroll: 'إرسال راتب {name}؟',
        payrollSubmitted: 'تم إرسال الراتب بنجاح',
        syncAccounting: 'مزامنة مع المحاسبة',
        syncedToAccounting: 'تمت المزامنة مع المحاسبة بنجاح',
        payrollDetails: 'تفاصيل الراتب',
        submit: 'إرسال',
        totalClaims: 'إجمالي المطالبات',
        totalAmount: 'المبلغ الإجمالي',
        addExpenseClaim: 'إضافة مطالبة مصروفات',
        expenseClaims: 'مطالبات المصروفات',
        claimedAmount: 'المبلغ المطالب به',
        sanctionedAmount: 'المبلغ المعتمد',
        createNewExpense: 'إنشاء مطالبة مصروفات جديدة',
        createClaim: 'إنشاء مطالبة',
        expenseDetails: 'تفاصيل المصروف',
        reimbursed: 'المبلغ المسترد',
        expenseCreated: 'تم إنشاء مطالبة المصروفات بنجاح',
        confirmApproveExpense: 'الموافقة على مصروفات {name}؟',
        expenseApproved: 'تمت الموافقة على المصروفات بنجاح',
        jobOpenings: 'الوظائف الشاغرة',
        totalApplicants: 'إجمالي المتقدمين',
        trainingEvents: 'الفعاليات التدريبية',
        holidays: 'العطلات',
        addJobOpening: 'إضافة وظيفة شاغرة',
        jobTitle: 'المسمى الوظيفي',
        department: 'القسم',
        postedOn: 'نشر في',
        applicants: 'المتقدمون',
        addApplicant: 'إضافة متقدم',
        applicantName: 'اسم المتقدم',
        email: 'البريد الإلكتروني',
        jobCreated: 'تم إنشاء الوظيفة الشاغرة بنجاح',
        applicantCreated: 'تم إنشاء المتقدم بنجاح',
        addDepartment: 'إضافة قسم',
        deptCode: 'رمز القسم',
        deptName: 'اسم القسم',
        headOfDept: 'رئيس القسم',
        notAssigned: 'غير معين',
        disabled: 'معطل',
        active: 'نشط',
        designations: 'المسميات الوظيفية',
        addDesignation: 'إضافة مسمى وظيفي',
        designationName: 'اسم المسمى الوظيفي',
        grades: 'الدرجات',
        addGrade: 'إضافة درجة',
        gradeName: 'اسم الدرجة',
        companies: 'الشركات',
        addCompany: 'إضافة شركة',
        companyName: 'اسم الشركة',
        editDepartment: 'تعديل القسم',
        deptUpdated: 'تم تحديث القسم بنجاح',
        deptCreated: 'تم إنشاء القسم بنجاح',
        deptDeleted: 'تم حذف القسم بنجاح',
        confirmDelete: 'هل أنت متأكد من الحذف؟',
        editDesignation: 'تعديل المسمى الوظيفي',
        desigCreated: 'تم إنشاء المسمى الوظيفي بنجاح',
        desigUpdated: 'تم تحديث المسمى الوظيفي بنجاح',
        desigDeleted: 'تم حذف المسمى الوظيفي بنجاح',
        editGrade: 'تعديل الدرجة',
        gradeCreated: 'تم إنشاء الدرجة بنجاح',
        gradeUpdated: 'تم تحديث الدرجة بنجاح',
        gradeDeleted: 'تم حذف الدرجة بنجاح',
        editCompany: 'تعديل الشركة',
        companyCreated: 'تم إنشاء الشركة بنجاح',
        companyUpdated: 'تم تحديث الشركة بنجاح',
        companyDeleted: 'تم حذف الشركة بنجاح',
        reportEmployeeReport: 'تقرير الموظفين',
        reportEmployeeDesc: 'قائمة كاملة بالموظفين مع التفاصيل',
        reportAttendanceReport: 'تقرير الحضور',
        reportAttendanceDesc: 'ملخص وتفاصيل الحضور',
        reportLeaveReport: 'تقرير الإجازات',
        reportLeaveDesc: 'رصيد وسجل الإجازات',
        reportPayrollReport: 'تقرير الرواتب',
        reportPayrollDesc: 'ملخص وتفاصيل الرواتب',
        reportExpenseReport: 'تقرير المصروفات',
        reportExpenseDesc: 'ملخص مطالبات المصروفات',
        reportRecruitmentReport: 'تقرير التوظيف',
        reportRecruitmentDesc: 'المتقدمون للوظائف والحالة',
        reportSalaryRegister: 'سجل الرواتب',
        reportSalaryRegisterDesc: 'سجل الرواتب الكامل',
        reportIncomeTax: 'حساب ضريبة الدخل',
        reportIncomeTaxDesc: 'تفاصيل حساب الضريبة',
        reportPaymentMode: 'تحليل طريقة الدفع',
        reportPaymentModeDesc: 'الدفع حسب الطريقة',
        employeeId: 'الرقم الوظيفي',
        designation: 'المسمى الوظيفي',
        hrSettings: 'إعدادات الموارد البشرية',
        settingsDesc: 'تكوين إعدادات وحدة الموارد البشرية',
        generalSettings: 'الإعدادات العامة',
        defaultCompany: 'الشركة الافتراضية',
        fiscalYear: 'السنة المالية',
        country: 'الدولة',
        currency: 'العملة',
        saudiArabia: 'المملكة العربية السعودية',
        jordan: 'الأردن',
        uae: 'الإمارات',
        other: 'أخرى',
        attendanceSettings: 'إعدادات الحضور',
        workingHours: 'ساعات العمل',
        lateThreshold: 'حد التأخر (بالدقائق)',
        leaveSettings: 'إعدادات الإجازات',
        annualLeaveDays: 'أيام الإجازة السنوية',
        sickLeaveDays: 'أيام الإجازة المرضية',
        settingsSaved: 'تم حفظ الإعدادات بنجاح',
      },
      // Accounting Module
      acc: {
        salesInvoices: 'فواتير المبيعات', purchaseInvoices: 'فواتير الشراء', paymentEntries: 'سندات القبض والصرف',
        journalEntries: 'القيود المحاسبية', customers: 'العملاء', suppliers: 'الموردون',
        chartOfAccounts: 'دليل الحسابات', costCenters: 'مراكز التكلفة', payroll: 'الرواتب',
        bankAccounts: 'الحسابات البنكية', taxCategories: 'فئات الضرائب', currencies: 'العملات',
        paymentTerms: 'شروط الدفع', reports: 'التقارير', trialBalance: 'ميزان المراجعة',
        cashFlow: 'التدفقات النقدية', caseFinancials: 'المالية القضايا', organization: 'الهيكل التنظيمي',
        accountsReceivable: 'الذمم المدينة', accountsPayable: 'الذمم الدائنة',
        balanceSheet: 'الميزانية العمومية', profitAndLoss: 'الأرباح والخسائر',
        recentInvoices: 'الفواتير الأخيرة', quickActions: 'إجراءات سريعة',
        newSalesInvoice: 'فاتورة مبيعات جديدة', newPurchaseInvoice: 'فاتورة شراء جديدة',
        recordPayment: 'تسجيل دفعة', newJournalEntry: 'قيد محاسبي جديد',
        manageCustomers: 'إدارة العملاء', viewReports: 'عرض التقارير',
        topCustomers: 'أفضل العملاء', topSuppliers: 'أفضل الموردين',
        totalSales: 'إجمالي المبيعات', totalPayments: 'إجمالي المدفوعات',
        totalPurchases: 'إجمالي المشتريات', outstandingAmount: 'المبلغ المستحق',
        customer: 'العميل', supplier: 'المورد', date: 'التاريخ', amount: 'المبلغ',
        status: 'الحالة', invoiceNo: 'رقم الفاتورة', invoice: 'الفاتورة',
        account: 'الحساب', accountName: 'اسم الحساب', accountNumber: 'رقم الحساب',
        debit: 'مدين', credit: 'دائن', total: 'الإجمالي',
        generate: 'إنشاء', generateReport: 'إنشاء تقرير',
        download: 'تحميل', noData: 'لا توجد بيانات',
        name: 'الاسم', type: 'النوع', description: 'الوصف', actions: 'الإجراءات',
        edit: 'تعديل', delete: 'حذف', save: 'حفظ', cancel: 'إلغاء',
        add: 'إضافة', create: 'إنشاء', active: 'نشط', disabled: 'معطل',
        yes: 'نعم', no: 'لا', none: 'لا شيء', pending: 'معلق', submitted: 'مرسل',
        allAccountTypes: 'جميع أنواع الحسابات', allStatus: 'جميع الحالات', allTypes: 'جميع الأنواع',
        category: 'الفئة', inflow: 'التدفق الداخل', outflow: 'التدفق الخارج', net: 'الصافي',
        dueDate: 'تاريخ الاستحقاق', outstanding: 'المستحق', age: 'العمر',
        bankName: 'اسم البنك', balance: 'الرصيد', currency: 'العملة', symbol: 'الرمز',
        precision: 'الدقة', currencyCode: 'رمز العملة', currencyName: 'اسم العملة',
        addBankAccount: 'إضافة حساب بنكي', addCurrency: 'إضافة عملة',
        addCostCenter: 'إضافة مركز تكلفة', editCostCenter: 'تعديل مركز التكلفة',
        parentCostCenter: 'مركز التكلفة الرئيسي', isGroup: 'مجموعة', manager: 'المدير',
        addPaymentTerm: 'إضافة شرط دفع', termName: 'اسم الشرط', dueDays: 'أيام الاستحقاق',
        addTaxCategory: 'إضافة فئة ضريبية', categoryName: 'اسم الفئة',
        addTaxRule: 'إضافة قاعدة ضريبية', taxRate: 'معدل الضريبة', taxCategory: 'الفئة الضريبية',
        addTaxTemplate: 'إضافة قالب ضريبي', templateName: 'اسم القالب',
        addWithholding: 'إضافة استقطاع', withholdingName: 'اسم الاستقطاع',
        withholdingRate: 'نسبة الاستقطاع', threshold: 'الحد الأدنى',
        addUser: 'إضافة مستخدم', userName: 'اسم المستخدم', email: 'البريد الإلكتروني', password: 'كلمة المرور',
        roles: 'الأدوار', roleName: 'اسم الدور', permissions: 'الصلاحيات',
        addRole: 'إضافة دور', addShop: 'إضافة متجر', shopName: 'اسم المتجر',
        addSubscription: 'إضافة اشتراك', plan: 'الخطة', monthlyAmount: 'المبلغ الشهري',
        nextBilling: 'الفوترة القادمة', frequency: 'التكرار',
        addRecurring: 'إضافة متكرر', startDate: 'تاريخ البدء', endDate: 'تاريخ الانتهاء',
        addDeferredRevenue: 'إضافة إيراد مؤجل', recognized: 'المعترف به', remaining: 'المتبقي',
        addExchangeRate: 'إضافة سعر صرف', fromCurrency: 'من عملة', toCurrency: 'إلى عملة',
        newRate: 'السعر الجديد', oldRate: 'السعر القديم', gainLoss: 'الربح/الخسارة',
        addFiscalYear: 'إضافة سنة مالية', yearName: 'اسم السنة', year: 'السنة', month: 'الشهر',
        accountingPeriods: 'الفترات المحاسبية', reopen: 'إعادة فتح',
        addInvoiceDiscounting: 'إضافة خصم فاتورة', invoiceAmount: 'مبلغ الفاتورة',
        addAssignment: 'إضافة مهمة', party: 'الطرف', paymentType: 'نوع الدفع',
        paidFrom: 'مدفوع من', paidTo: 'مدفوع إلى', reference: 'المرجع',
        addItem: 'إضافة عنصر', itemCode: 'رمز العنصر', itemGroup: 'مجموعة العناصر',
        available: 'متاح', rate: 'المعدل', qty: 'الكمية',
        addSalary: 'إضافة راتب', salaryComponents: 'مكونات الراتب',
        salaryStructures: 'هياكل الرواتب', salarySlips: 'قسائم الرواتب',
        payrollEntries: 'قيود الرواتب', newPayrollEntry: 'قيد راتب جديد',
        salaryStructure: 'هيكل الراتب', refNo: 'الرقم المرجعي', postingDate: 'تاريخ الترحيل',
        grossPay: 'إجمالي الراتب', netPay: 'صافي الراتب', period: 'الفترة',
        calculationType: 'نوع الحساب', effectiveFrom: 'ساري من',
        company: 'الشركة', abbreviation: 'الاختصار', country: 'الدولة',
        parentDepartment: 'القسم الرئيسي', departments: 'الأقسام', branches: 'الفروع',
        designations: 'المسميات الوظيفية', setupWizard: 'معالج الإعداد', completed: 'مكتمل',
        newEntry: 'إدخال جديد', newCustomer: 'عميل جديد', newSupplier: 'مورد جديد',
        address: 'العنوان', phone: 'الهاتف', taxId: 'الرقم الضريبي', supplierType: 'نوع المورد',
        searchInvoices: 'البحث في الفواتير...', searchPayments: 'البحث في المدفوعات...',
        selectCase: 'اختر القضية', selectCaseToView: 'اختر قضية لعرض التفاصيل المالية',
        linkedCase: 'القضية المرتبطة', noCaseLink: 'لا توجد قضية مرتبطة',
        bankReconciliation: 'التسوية البنكية', matchTransactions: 'مطابقة المعاملات',
        matched: 'مطابق', unmatched: 'غير مطابق', matchedWith: 'مطابق مع',
        reconciliation: 'التسوية', bankStatement: 'كشف الحساب البنكي',
        periodClosure: 'إغلاق الفترة', closePeriod: 'إغلاق الفترة',
        auditTrail: 'سجل المراجعة', timestamp: 'الطابع الزمني', user: 'المستخدم', action: 'الإجراء',
        consolidation: 'التوحيد', runConsolidation: 'تشغيل التوحيد',
        variance: 'التباين', utilization: 'الاستخدام',
        incomeTaxSlabs: 'شرائح ضريبة الدخل', addSlab: 'إضافة شريحة', taxSlabName: 'اسم الشريحة',
        hrSyncLog: 'سجل مزامنة الموارد البشرية', syncNow: 'مزامنة الآن', syncStatus: 'حالة المزامنة',
        pendingSync: 'مزامنة معلقة', noSyncLogs: 'لا توجد سجلات مزامنة',
        totalInvoices: 'إجمالي الفواتير', totalExpenses: 'إجمالي المصروفات',
        totalCollections: 'إجمالي التحصيلات', totalDebit: 'إجمالي المدين', totalCredit: 'إجمالي الدائن',
        netBalance: 'صافي الرصيد', netAmount: 'صافي المبلغ', totalAmount: 'إجمالي المبلغ',
        overdueInvoices: 'الفواتير المتأخرة', open: 'مفتوح', settled: 'مسوى', stopped: 'متوقف',
        never: 'أبدا', nextDate: 'التاريخ القادم', lastRun: 'آخر تشغيل',
        sendLetter: 'إرسال خطاب', withdraw: 'سحب', withdrawal: 'السحب',
        mrr: 'الإيرادات المتكررة', arDesc: 'تحليل أعمار الذمم المدينة', apDesc: 'تحليل أعمار الذمم الدائنة',
        trialBalanceDesc: 'ملخص أرصدة الحسابات', balanceSheetDesc: 'تقرير المركز المالي',
        profitLossDesc: 'ملخص الإيرادات والمصروفات', journalEntriesDesc: 'القيود المحاسبية اليومية',
        viewAll: 'عرض الكل', priority: 'الأولوية',
        inactive: 'غير نشط', all: 'الكل', from: 'من', to: 'إلى',
        fromDate: 'من تاريخ', toDate: 'إلى تاريخ', actual: 'فعلي', budget: 'الميزانية',
        budgetVsActual: 'الميزانية مقابل الفعلي', fromIncome: 'من الإيرادات', toIncome: 'إلى الإيرادات',
        addLine: 'إضافة سطر', items: 'العناصر', accounts: 'الحسابات',
        salesInvoicesDesc: 'إدارة فواتير المبيعات', purchaseInvoicesDesc: 'إدارة فواتير الشراء',
        paymentsDesc: 'تتبع سندات القبض والصرف', journalDesc: 'إدارة القيود المحاسبية',
        customersDesc: 'إدارة العملاء', suppliersDesc: 'إدارة الموردين',
        chartDesc: 'دليل الحسابات', costCentersDesc: 'إدارة مراكز التكلفة',
        payrollDesc: 'إدارة الرواتب', bankDesc: 'إدارة الحسابات البنكية',
        taxDesc: 'فئات الضرائب', currenciesDesc: 'إدارة العملات',
        paymentTermsDesc: 'شروط الدفع', reportsDesc: 'التقارير المالية',
      },
    }
  }
})

const app = createApp(App)
const pinia = createPinia()
app.use(pinia)
app.use(i18n)
app.use(router)
app.use(PrimeVue, { locale: { startsWith: 'Starts with', contains: 'Contains' } })
app.use(ToastService)
app.use(ConfirmationService)
app.component('Toast', Toast)
app.component('ConfirmDialog', ConfirmDialog)
app.component('Button', Button)

// Restore auth state from localStorage
import { useAuthStore } from '@/stores/auth'
const authStore = useAuthStore()
authStore.init()

app.mount('#app')
